import React, { useMemo, lazy } from "react";
import { ThemeProvider } from "@emotion/react";
import { withRouter, useLocation } from "react-router";
import { Route } from "./utils/router";
import Layout from "./Layout/Layout";
import Login from "./Pages/Login/Login";
import PrivateRoute from "./Pages/PrivateRoute/PrivateRoute";
import { useAppStateConfig } from "./hooks/useAppStateConfig";
import { Helmet } from "react-helmet";
import AdminLayout from "./Layout/Admin/AdminLayout";
import Loading from "./UI/Loading/Loading";
import Landing from "./Pages/Landing/Landing";
import words from "./styles/words";

import "./App.css";
import TechnicalMessage from "./Pages/TechnicalMessage/ThechnicalMessage";
import Toaster from "./Editors/Toaster/Toaster";
import Logout from "./Pages/Logout/Logout";
import ScheduleToast from "./Editors/Toaster/ScheduleToast";
import { parse } from "qs";
import ExternalGoogleIdTokenPage from "./Pages/Login/Google/ExternalGoogleIdTokenPage";
import ProductionWarning from "./UI/ProductionWarning";
import { showProductionWarning } from "./utils/env";
import { useSelector } from "react-redux";
import { NETWORK_ERROR, NOT_FOUND } from "./utils/errorHandling";
import SwitchWithSuspenseFallback from "./etc/SwitchWithSuspenseFallback";
import AppBoundary from "./AppBoundary";
import useDailySessionRefresh from "./hooks/useDailySessionRefresh";
import { useMustConfigure2FA } from "./hooks/authHooks";
import DebugFeaturesOverlay from "./etc/DebugFeature/DebugFeaturesOverlay";

const TableEditor = lazy(() => import("./Pages/TableEditor/TableEditor"));

const App = (props) => {
  const {
    siteName,
    favicon,
    theme,
    landing,
    loading,
    themeName,
    invalidDomain,
  } = useAppStateConfig(props);

  const location = useLocation();
  const query = useMemo(
    () => parse(location.search, { ignoreQueryPrefix: true }),
    [location.search]
  );
  const { isAuthenticated, isAuthenticationComplete, error } = useSelector(
    (state) => state.auth
  );
  useDailySessionRefresh();
  const mustConfigure2FA = useMustConfigure2FA();

  if ((invalidDomain?.type ?? error?.type) === NETWORK_ERROR) {
    return <TechnicalMessage message={words.network_error} />;
  }
  if (invalidDomain?.type === NOT_FOUND)
    return <TechnicalMessage message={words.domain_not_found} />;
  if (invalidDomain) return <TechnicalMessage message={words.not_configured} />;
  if (loading && query.googleIdToken) {
    return <ExternalGoogleIdTokenPage />;
  }
  if (landing) return <Landing />;
  if (loading || !themeName || (isAuthenticated && !isAuthenticationComplete))
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
        <Loading />
      </div>
    );

  const routeConfig = [
    {
      allowedForGuests: true,
      path: "/login",
      component: Login,
    },
    {
      path: "/logout",
      exact: true,
      component: Logout,
    },
    ...(!mustConfigure2FA
      ? [
          {
            path: "/admin",
            component: AdminLayout,
          },
          {
            path: "/table-editor/pages/:pageUuid/visualizations/:visualizationUuid",
            component: TableEditor,
          },
        ]
      : []),
    {
      path: "/",
      component: Layout,
    },
  ];

  return (
    <div className="App">
      <Helmet>
        <title>{siteName}</title>
        <link
          rel="icon"
          type="image/png"
          href={
            favicon
              ? favicon
              : "https://s3.amazonaws.com/dev.insightoutstatic/demo/favicon.png"
          }
        />
      </Helmet>

      <ThemeProviderAndAppBoundary theme={theme}>
        <div id="modal-portal" style={{ position: "relative", zIndex: 5000 }} />
        <div
          id="tipper-portal"
          style={{ position: "relative", zIndex: 1000000 }}
        />
        <DebugFeaturesOverlay />

        <SwitchWithSuspenseFallback>
          {routeConfig.map(({ allowedForGuests, ...props }) => {
            const RouteComponent = allowedForGuests ? Route : PrivateRoute;

            return <RouteComponent key={props.path} {...props} />;
          })}
        </SwitchWithSuspenseFallback>
        <Toaster />
        <ScheduleToast />
      </ThemeProviderAndAppBoundary>
      {showProductionWarning && <ProductionWarning />}
    </div>
  );
};

function ThemeProviderAndAppBoundary({ theme, children }) {
  return (
    <ThemeProvider theme={theme}>
      <AppBoundary>{children}</AppBoundary>
    </ThemeProvider>
  );
}

export default withRouter(App);
